import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { showToast } from 'shared/ui'
import { BillingApi } from 'entities/Billing/api/billing'
import { BillingPath, NumbersPath } from 'entities/ExportRouters'
import { type SubscriptionStore } from 'entities/Subscription/store/subscription'
import { type FeatureFlagsStore } from 'entities/FeatureFlags'
import { openIntercom } from 'entities/Intercom'
import { type OverviewPageStore } from 'pages/settings/pages/billing/pages/overview/store/overviewPageStore'

export const getIntervalLabel = (interval: string | null) => {
  if (interval === null || interval === 'monthly') {
    return 'month'
  }

  if (interval === 'quarterly') {
    return '3 months'
  }

  if (interval === 'semi-annual') {
    return '6 months'
  }

  if (interval === 'annual') {
    return 'year'
  }

  return interval
}

export class PlanInfoStore {
  private _isSuggestionAlertDismissed = false
  private _isCancelingDowngrade = false
  private _isResumingSubscription = false

  constructor(
    private _subscriptionStore: SubscriptionStore,
    private _overviewStore: OverviewPageStore,
    private _featureFlagsStore: FeatureFlagsStore
  ) {
    makeAutoObservable(this)
  }

  get isSuggestionAlertDismissed() {
    return this._isSuggestionAlertDismissed
  }

  get isCancelingDowngrade() {
    return this._isCancelingDowngrade
  }

  get isCustomPlan() {
    return this._subscriptionStore.plan?.custom ?? false
  }

  get upgradeButtonId() {
    return this.isCustomPlan && !this._subscriptionStore.subscriptionSuggestion
      ? 'chat-with-sales-button'
      : ''
  }

  onUpgradeClick = () => {
    if (this._subscriptionStore.subscriptionSuggestion) {
      if (this._featureFlagsStore.billing_overview_v2) {
        this._overviewStore.openOpportunityModal()
      } else {
        uiStore.changeRouteViaVue({
          path: `/${BillingPath.upgrade}`,
          options: {
            state: {
              checkOpportunity: true,
            },
          },
        })
      }
      return
    }

    if (this.isCustomPlan) {
      openIntercom()
      return
    }

    uiStore.changeRouteViaVue({
      path: `/${BillingPath.upgrade}`,
    })
  }

  onCancelDowngradeClick = async () => {
    if (this._isCancelingDowngrade) return

    this._isCancelingDowngrade = true

    try {
      await BillingApi.cancelUpgradePlan()
      await this._subscriptionStore.init()

      showToast({
        title: 'Downgrade canceled',
        type: 'success',
      })
    } catch (e) {
      logger.error(e)
      showToast({
        title: 'Failed to cancel downgrade',
        type: 'error',
      })
    } finally {
      runInAction(() => {
        this._isCancelingDowngrade = false
      })
    }
  }

  onSeeOfferClick = () => {
    this._overviewStore.openOpportunityModal()
  }

  onKeepPlanClick = async () => {
    if (this._isResumingSubscription) return

    this._isResumingSubscription = true

    try {
      const { data } = await BillingApi.resumeSubscription()
      if (data.resumed) {
        this._subscriptionStore.setIsScheduledCancellation(false)
      }

      showToast({
        title: 'Subscription kept',
        desc: 'Your plan will continue as normal',
        type: 'success',
      })
    } catch (e) {
      logger.error(e)
      showToast({
        title: 'Failed to resume subscription',
        type: 'error',
      })
    } finally {
      runInAction(() => {
        this._isResumingSubscription = false
      })
    }
  }

  onDismissSuggestionAlert = () => {
    this._isSuggestionAlertDismissed = true
  }

  onManageNumbersClick = () => {
    uiStore.changeRouteViaVue({
      path: `/${NumbersPath.root}`,
    })
  }
}
